/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { css as cssUnbounded, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import Lozenge from '@atlaskit/lozenge';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	container: {
		// Match AICollapse's header padding (space.200 left/right, space.150 top) and
		// minHeight (40px) so toggling between the expanded card heading and this
		// minimized ready state does not cause a vertical/horizontal layout shift.
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		minHeight: '40px',
		boxSizing: 'border-box',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
	},
	answerLabel: {
		flexShrink: 0,
	},
	containerWithShadow: {
		boxShadow: token('elevation.shadow.overlay'),
	},
});

// Optical nudge to vertically align the Lozenge text with adjacent body text.
const lozengeWrapperStyle = cssUnbounded({
	display: 'flex',
	alignItems: 'center',
	position: 'relative',
	top: '1px',
});

const messages = defineMessages({
	answer: {
		id: 'search-ai.smart-answer-card-minimized-ready.answer',
		defaultMessage: 'Answer',
		description: 'Label shown in the minimized ready state indicating an AI answer is available',
	},
	ready: {
		id: 'search-ai.smart-answer-card-minimized-ready.ready',
		defaultMessage: 'Ready',
		description: 'Lozenge text shown when the AI answer is ready',
	},
	scroll_up: {
		id: 'search-ai.smart-answer-card-minimized-ready.scroll-up',
		defaultMessage: 'Scroll up',
		description: 'Button label to scroll up to view the answer',
	},
	expand_answer: {
		id: 'search-ai.smart-answer-card-minimized-ready.expand-answer',
		defaultMessage: 'Expand',
		description: 'Button label to expand/show the hidden smart answer card',
	},
});

type SmartAnswerReadyProps = {
	onScrollUpClick?: () => void;
	onExpandClick?: () => void;
	/**
	 * Component expand answer state or floating answer ready state
	 */
	appearance?: 'expand' | 'scrollUp';
	testId?: string;
};

export function SmartAnswerReady({
	onScrollUpClick,
	onExpandClick,
	appearance,
	testId,
}: SmartAnswerReadyProps): React.JSX.Element {
	const { formatMessage } = useIntl();

	const resolvedAppearance: 'expand' | 'scrollUp' =
		appearance ?? (onExpandClick ? 'expand' : 'scrollUp');

	return (
		<Inline
			alignBlock="center"
			space="space.100"
			xcss={cx(styles.container, resolvedAppearance === 'scrollUp' && styles.containerWithShadow)}
			grow="fill"
			testId={testId}
		>
			<Inline alignBlock="center" space="space.100" grow="fill">
				<Box xcss={styles.answerLabel}>
					<AiGenerativeTextSummaryIcon label="" size="medium" />
				</Box>
				<Text size="medium" weight="medium">
					{formatMessage(messages.answer)}
				</Text>
				<div css={lozengeWrapperStyle}>
					<Lozenge appearance="success">{formatMessage(messages.ready)}</Lozenge>
				</div>
			</Inline>
			{resolvedAppearance === 'expand' ? (
				<Button
					onClick={onExpandClick}
					iconAfter={(iconProps) => <EyeOpenIcon {...iconProps} />}
					spacing="compact"
				>
					<Text color="color.text.subtlest">{formatMessage(messages.expand_answer)}</Text>
				</Button>
			) : (
				<Button
					onClick={onScrollUpClick}
					iconAfter={(iconProps) => <ArrowUpIcon {...iconProps} />}
					spacing="compact"
				>
					<Text color="color.text.subtlest">{formatMessage(messages.scroll_up)}</Text>
				</Button>
			)}
		</Inline>
	);
}
